/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

import { useSeo } from "@/lib/seo";

export default function Contact() {
  useSeo(
    "Contact Us | Pit Stop Motors — Nampa, Idaho",
    "Visit Pit Stop Motors at 3323 Port St. #203, Nampa, ID. Call, text, or send a message — we answer fast."
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        type: "contact",
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
      });
      toast.success("Thank you! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-24">
          <p className="overline !text-accent-bright">Get In Touch</p>
          <h1 className="mt-3 font-serif text-5xl md:text-6xl tracking-tight leading-none">Let's talk.</h1>
          <p className="mt-5 max-w-xl text-primary-foreground/70 leading-relaxed">
            Have a question about a vehicle? Want to schedule a visit? Drop us a note and someone from our team will reach out personally.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-8" data-testid="contact-info">
          <div>
            <p className="overline">Visit Our Lot</p>
            <h2 className="mt-3 font-serif text-3xl text-primary">We'd love to meet you.</h2>
          </div>
          {[
            { Icon: MapPin, title: "Address", lines: [BRAND.addressLine1, BRAND.addressLine2], href: BRAND.mapsHref, external: true },
            { Icon: Phone, title: "Phone", lines: [BRAND.phone], href: BRAND.phoneHref },
            { Icon: Mail, title: "Email", lines: [BRAND.email], href: `mailto:${BRAND.email}` },
            { Icon: Clock, title: "Hours", lines: ["Mon – Sat: 9am – 7pm", "Sunday: Closed"] },
          ].map((item) => (
            <div key={item.title} className="flex gap-4">
              <span className="flex h-10 w-10 items-center justify-center bg-secondary">
                <item.Icon className="h-4 w-4 text-accent" strokeWidth={1.5} />
              </span>
              <div>
                <p className="font-serif text-xl text-primary">{item.title}</p>
                {item.lines.map((l) =>
                  item.href ? (
                    <a
                      key={l}
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noreferrer" : undefined}
                      className="block text-sm text-muted-foreground hover:text-accent transition-colors break-all"
                    >
                      {l}
                    </a>
                  ) : (
                    <p key={l} className="text-sm text-muted-foreground">{l}</p>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-7" data-testid="contact-form-wrap">
          <div className="border border-soft bg-card p-8 md:p-10">
            <h3 className="font-serif text-3xl text-primary">Send us a message</h3>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-name">Name</Label>
                  <Input
                    id="c-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input
                    id="c-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="c-email">Email</Label>
                <Input
                  id="c-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Label htmlFor="c-message">Message</Label>
                <Textarea
                  id="c-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                size="lg"
                className="rounded-sm bg-accent text-accent-foreground hover:bg-accent/90"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
